import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography, Box, TextField, Chip, Stack, Button, InputAdornment, Divider, Tooltip } from "@mui/material";
import { Search as SearchIcon, Add, PlayArrow, Edit, Delete } from "@mui/icons-material";
import styled from "@emotion/styled";
import { getTemplates, executeGroupTemplate, executeProjectTemplate, deleteGroupTemplate, deleteProjectTemplate, deleteTaskTemplate, deleteChecklistTemplate, deleteQuoteTemplate, getGroupTemplate, getProjectTemplate, getTaskTemplate, getChecklistTemplate, getQuoteTemplate } from "../redux/ducks/template";
import { useDispatch } from "react-redux";
import { useHasPermission } from "../utils/permissionUtils";
import MarkdownDisplay from "../components/MarkdownDisplay";
import TemplateExecuteModal from "../components/TemplateExecuteModal";
import TemplateEditorWizard from "../components/templates/TemplateEditorWizard";
import ConfirmDialog from "../components/admin/ConfirmDialog";
import TemplateTreeView, { missingFields } from "../components/TemplateTreeView";
import PageHeader from "../components/PageHeader";
import { tagDisplayName } from "../components/TagPicker";
import { formatDate as formatDay } from "../utils/dateUtils";

const PageContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    width: "100%",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3, 0, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ContentArea = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
    },
}));

const TreePanel = styled(Box)(({ theme }) => ({
    width: 380,
    minWidth: 300,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        width: "100%",
        maxHeight: "40vh",
        borderRight: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}));

const TreeSearch = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TreeContent = styled(Box)({
    flex: 1,
    overflow: "auto",
});

const DetailPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(3),
}));

const DetailCard = styled(Box)(({ theme }) => ({
    maxWidth: 800,
}));

const DetailHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const DetailSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const MissingValue = styled(Typography)(({ theme }) => ({
    color: theme.palette.warning.main,
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: theme.typography.size.sm,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const EmptyDetail = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.palette.text.secondary,
}));

const SearchAdornment = styled(InputAdornment)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const DetailTitleRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const NamePatternText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: theme.typography.size.smd,
}));

const DetailDivider = styled(Divider)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
}));

const QuoteMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
}));

const SchedulingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
}));

const TotalPaymentText = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const ChecklistHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StepLine = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(0.25, 0),
}));

const getTypeColor = (type) => {
    switch (type) {
        case "group": return "primary";
        case "project": return "secondary";
        case "task": return "success";
        case "checklist": return "warning";
        case "quote": return "info";
        default: return "default";
    }
};

const Templates = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const [templates, setTemplates] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [executeModalOpen, setExecuteModalOpen] = useState(false);
    const [executing, setExecuting] = useState(false);
    // State is batched, so a synchronous double-click reads the old value; the ref updates immediately.
    const executingRef = useRef(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [detailTemplate, setDetailTemplate] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState("create");
    const [editorType, setEditorType] = useState("group");
    const [editorTemplate, setEditorTemplate] = useState(null);
    const [editorParent, setEditorParent] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const canExecuteTemplates = hasPermission("executeTemplates");
    const canManageTemplates = hasPermission("manageTemplates");
    const canManageQuoteTemplates = hasPermission("canManageQuotes");
    // Quote templates are managed under canManageQuotes, not manageTemplates — every other type keeps the general gate.
    const canManageNode = (templateType) => (templateType === "quote" ? canManageQuoteTemplates : canManageTemplates);

    const loadTemplates = () => {
        dispatch(getTemplates(null, 0, 100, (data) => {
            setTemplates(data || []);
        }));
    };

    const filteredTemplates = useMemo(() => {
        if (!searchTerm) return templates;
        const term = searchTerm.toLowerCase();
        return templates.filter(
            (t) =>
                t.name?.toLowerCase().includes(term) ||
                t.displayName?.toLowerCase().includes(term) ||
                t.overview?.toLowerCase().includes(term) ||
                t.description?.toLowerCase().includes(term)
        );
    }, [templates, searchTerm]);

    useEffect(() => {
        loadTemplates();
    }, [dispatch]);

    // Select a template in the tree and load full details
    const handleSelect = (template) => {
        setDetailTemplate(template);
        // Fetch full template for detail view
        const cb = (data) => {
            if (!data) return;
            setDetailTemplate({ ...data, templateType: template.templateType });
        };
        if (template.templateType === "group") {
            dispatch(getGroupTemplate(template._id, cb));
        } else if (template.templateType === "project") {
            dispatch(getProjectTemplate(template._id, cb));
        } else if (template.templateType === "checklist") {
            dispatch(getChecklistTemplate(template._id, cb));
        } else if (template.templateType === "quote") {
            dispatch(getQuoteTemplate(template._id, cb));
        } else {
            dispatch(getTaskTemplate(template._id, cb));
        }
    };

    const detailMissing = missingFields(detailTemplate);

    const handleExecute = (template) => {
        setSelectedTemplate(template);
        setExecuteModalOpen(true);
    };

    const handleExecuteConfirm = (options) => {
        if (executingRef.current) return;

        const done = (result) => {
            executingRef.current = false;
            setExecuting(false);
            if (!result) return;
            setExecuteModalOpen(false);
            setSelectedTemplate(null);
            loadTemplates();
        };

        if (selectedTemplate.templateType !== "group" && selectedTemplate.templateType !== "project") return;

        executingRef.current = true;
        setExecuting(true);
        if (selectedTemplate.templateType === "group") {
            dispatch(executeGroupTemplate(selectedTemplate._id, options, done));
        } else {
            dispatch(executeProjectTemplate(selectedTemplate._id, options, done));
        }
    };

    const handleDelete = (template) => {
        setDeleteTarget(template);
    };

    const confirmDelete = () => {
        const template = deleteTarget;
        setDeleteTarget(null);
        if (detailTemplate?._id === template._id) {
            setDetailTemplate(null);
        }
        if (template.templateType === "group") {
            dispatch(deleteGroupTemplate(template._id, () => loadTemplates()));
        } else if (template.templateType === "project") {
            dispatch(deleteProjectTemplate(template._id, () => loadTemplates()));
        } else if (template.templateType === "task") {
            dispatch(deleteTaskTemplate(template._id, () => loadTemplates()));
        } else if (template.templateType === "checklist") {
            dispatch(deleteChecklistTemplate(template._id, () => loadTemplates()));
        } else if (template.templateType === "quote") {
            dispatch(deleteQuoteTemplate(template._id, () => loadTemplates()));
        }
    };

    // Contextual create: "Add Child" on a group creates a project, on a project creates a task
    const handleAddChild = (parentTemplate) => {
        if (parentTemplate.templateType === "group") {
            setEditorType("project");
            setEditorParent({ group: parentTemplate._id });
        } else if (parentTemplate.templateType === "project") {
            setEditorType("task");
            setEditorParent({ project: parentTemplate._id });
        }
        setEditorMode("create");
        setEditorTemplate(null);
        setEditorOpen(true);
    };

    // Create new root-level group template
    const handleCreateRoot = () => {
        setEditorMode("create");
        setEditorType("group");
        setEditorTemplate(null);
        setEditorParent(null);
        setEditorOpen(true);
    };

    // Create new standalone project template (no group)
    const handleCreateProject = () => {
        setEditorMode("create");
        setEditorType("project");
        setEditorTemplate(null);
        setEditorParent(null);
        setEditorOpen(true);
    };

    const handleCreateChecklist = () => {
        setEditorMode("create");
        setEditorType("checklist");
        setEditorTemplate(null);
        setEditorParent(null);
        setEditorOpen(true);
    };

    const handleCreateQuoteTemplate = () => {
        setEditorMode("create");
        setEditorType("quote");
        setEditorTemplate(null);
        setEditorParent(null);
        setEditorOpen(true);
    };

    const openEdit = (template) => {
        setEditorMode("edit");
        setEditorType(template.templateType);
        setEditorParent(null);
        const cb = (data) => {
            if (!data) return;
            setEditorTemplate(data);
            setEditorOpen(true);
        };
        if (template.templateType === "group") {
            dispatch(getGroupTemplate(template._id, cb));
        } else if (template.templateType === "project") {
            dispatch(getProjectTemplate(template._id, cb));
        } else if (template.templateType === "checklist") {
            dispatch(getChecklistTemplate(template._id, cb));
        } else if (template.templateType === "quote") {
            dispatch(getQuoteTemplate(template._id, cb));
        } else {
            dispatch(getTaskTemplate(template._id, cb));
        }
    };

    const searchAdornment = useMemo(
        () => (
            <SearchAdornment position="start">
                <SearchIcon />
            </SearchAdornment>
        ),
        []
    );

    const formatDate = (dateVal) => formatDay(dateVal) || null;

    const formatRelativeAnchor = (anchor) => {
        const labels = {
            startOfMonth: "Start of month",
            endOfMonth: "End of month",
            startOfNextMonth: "Start of next month",
            endOfNextMonth: "End of next month",
            startOfQuarter: "Start of quarter",
            endOfQuarter: "End of quarter",
        };
        return labels[anchor] || anchor;
    };

    return (
        <PageContainer>
            <HeaderSection>
                <PageHeader
                    title="Templates"
                    actions={(canManageTemplates || canManageQuoteTemplates) ? (
                        <Stack direction="row" spacing={1}>
                            {canManageTemplates && (
                                <>
                                    <Button variant="contained" startIcon={<Add />} onClick={handleCreateRoot}>
                                        New Group Template
                                    </Button>
                                    <Button variant="outlined" startIcon={<Add />} onClick={handleCreateProject}>
                                        New Project Template
                                    </Button>
                                    <Tooltip title="A reusable list of steps you import into a task's checklist. It cannot be run on its own.">
                                        <Button variant="outlined" startIcon={<Add />} onClick={handleCreateChecklist}>
                                            New Checklist Template
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                            {canManageQuoteTemplates && (
                                <Tooltip title="A standard engagement — the same tasks, hours and prices every time. The AI picks one when it matches a client's request, and a manager can apply one to a quote by hand.">
                                    <Button variant="outlined" startIcon={<Add />} onClick={handleCreateQuoteTemplate}>
                                        New Quote Template
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    ) : null}
                />
            </HeaderSection>

            <ContentArea>
                <TreePanel>
                    <TreeSearch>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: searchAdornment }}
                        />
                    </TreeSearch>
                    <TreeContent>
                        <TemplateTreeView
                            templates={filteredTemplates}
                            selectedId={detailTemplate?._id}
                            onSelect={handleSelect}
                            onAddChild={handleAddChild}
                            onExecute={handleExecute}
                            onEdit={openEdit}
                            onDelete={handleDelete}
                            canManage={canManageNode}
                            canExecute={canExecuteTemplates}
                        />
                    </TreeContent>
                </TreePanel>

                <DetailPanel>
                    {detailTemplate ? (
                        <DetailCard>
                            <DetailHeader>
                                <Box>
                                    <DetailTitleRow>
                                        <Typography variant="h5">{detailTemplate.displayName || detailTemplate.name}</Typography>
                                        <Chip
                                            label={detailTemplate.templateType}
                                            color={getTypeColor(detailTemplate.templateType)}
                                            size="small"
                                        />
                                    </DetailTitleRow>
                                    {detailTemplate.displayName && (
                                        <NamePatternText variant="body2" color="text.secondary">
                                            Name pattern: {detailTemplate.name}
                                        </NamePatternText>
                                    )}
                                    {detailTemplate.group?.name && (
                                        <Typography variant="body2" color="text.secondary">
                                            Group: {detailTemplate.group.name}
                                        </Typography>
                                    )}
                                    {detailTemplate.project?.name && (
                                        <Typography variant="body2" color="text.secondary">
                                            Project: {detailTemplate.project.name}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    {(detailTemplate.templateType === "group" || detailTemplate.templateType === "project") && canExecuteTemplates && (
                                        <Button size="small" variant="outlined" startIcon={<PlayArrow />} onClick={() => handleExecute(detailTemplate)}>
                                            Execute
                                        </Button>
                                    )}
                                    {canManageNode(detailTemplate.templateType) && (
                                        <>
                                            <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => openEdit(detailTemplate)}>
                                                Edit
                                            </Button>
                                            <Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={() => handleDelete(detailTemplate)}>
                                                Delete
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </DetailHeader>

                            <DetailDivider />

                            {detailTemplate.overview && (
                                <DetailSection>
                                    <DetailLabel>Overview</DetailLabel>
                                    <MarkdownDisplay content={detailTemplate.overview} />
                                </DetailSection>
                            )}

                            {detailTemplate.templateType === "checklist" && (
                                <>
                                    <DetailSection>
                                        <DetailLabel>Description</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.description || "No description."}</Typography>
                                    </DetailSection>
                                    <DetailSection>
                                        <DetailLabel>Steps ({detailTemplate.items?.length || 0})</DetailLabel>
                                        <ChecklistHelper variant="body2" color="text.secondary">
                                            This template can't be run. Open a task and use "Import from template" to add these steps to its checklist.
                                        </ChecklistHelper>
                                        {detailTemplate.items?.length > 0 ? (
                                            [...detailTemplate.items]
                                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                                .map((item, idx) => (
                                                    <StepLine key={item._id || idx} variant="body2">
                                                        {idx + 1}. {item.name}
                                                    </StepLine>
                                                ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No steps yet. Edit this template to add some.
                                            </Typography>
                                        )}
                                    </DetailSection>
                                </>
                            )}

                            {detailTemplate.templateType === "quote" && (
                                <>
                                    <DetailSection>
                                        <DetailLabel>Description</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.description || "No description."}</Typography>
                                    </DetailSection>
                                    <DetailSection>
                                        <DetailLabel>Status</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.isActive === false ? "Retired — kept for reference, never picked for a new quote" : "Active — the AI and your team can pick this template"}</Typography>
                                    </DetailSection>
                                    <DetailSection>
                                        <DetailLabel>Line Items ({detailTemplate.lineItems?.length || 0})</DetailLabel>
                                        {detailTemplate.lineItems?.length > 0 ? (
                                            detailTemplate.lineItems.map((item, idx) => (
                                                <StepLine key={item._id || idx} variant="body2">
                                                    {item.title || "Untitled line"} — {item.hours ?? 0}h
                                                    {item.workType?.name ? ` · ${item.workType.name}` : ""}
                                                    {item.isActionable ? " · creates a task" : ""}
                                                </StepLine>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No line items yet. Edit this template to add some.
                                            </Typography>
                                        )}
                                    </DetailSection>
                                    <QuoteMetaRow>
                                        <Box>
                                            <DetailLabel>Project Type</DetailLabel>
                                            <Typography variant="body2">{detailTemplate.projectType || "Not set"}</Typography>
                                        </Box>
                                        <Box>
                                            <DetailLabel>Work Type</DetailLabel>
                                            <Typography variant="body2">{detailTemplate.skill?.name || "Not set"}</Typography>
                                        </Box>
                                        <Box>
                                            <DetailLabel>Billing Type</DetailLabel>
                                            <Typography variant="body2">{detailTemplate.billingType || "Not set"}</Typography>
                                        </Box>
                                        <Box>
                                            <DetailLabel>Extra Days</DetailLabel>
                                            <Typography variant="body2">{detailTemplate.extraDays ?? 0} days added to the estimate</Typography>
                                        </Box>
                                        <Box>
                                            <DetailLabel>Days Before Start</DetailLabel>
                                            <Typography variant="body2">{detailTemplate.daysBeforeStart ?? 0} days before work begins</Typography>
                                        </Box>
                                    </QuoteMetaRow>
                                    {["assumptions", "inclusions", "exclusions"].map((key) => (
                                        detailTemplate[key]?.length > 0 && (
                                            <DetailSection key={key}>
                                                <DetailLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</DetailLabel>
                                                {detailTemplate[key].map((text, idx) => (
                                                    <StepLine key={idx} variant="body2">• {text}</StepLine>
                                                ))}
                                            </DetailSection>
                                        )
                                    ))}
                                </>
                            )}

                            {detailTemplate.templateType !== "quote" && (
                            <MetaRow>
                                {detailTemplate.priority && (
                                    <Box>
                                        <DetailLabel>Priority</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.priority}</Typography>
                                    </Box>
                                )}
                                {detailTemplate.privacy && (
                                    <Box>
                                        <DetailLabel>Privacy</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.privacy}</Typography>
                                    </Box>
                                )}
                                {(detailTemplate.billingType || detailTemplate.billingTerms?.type) && (
                                    <Box>
                                        <DetailLabel>Billing Type</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.billingType || detailTemplate.billingTerms?.type}</Typography>
                                    </Box>
                                )}
                                {detailTemplate.status && (
                                    <Box>
                                        <DetailLabel>Status</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.status}</Typography>
                                    </Box>
                                )}
                                {detailTemplate.readiness && (
                                    <Box>
                                        <DetailLabel>Readiness</DetailLabel>
                                        <Typography variant="body2">{detailTemplate.readiness}</Typography>
                                    </Box>
                                )}
                                {(detailTemplate.department || detailMissing.includes("department")) && (
                                    <Box>
                                        <DetailLabel>Department</DetailLabel>
                                        {detailTemplate.department ? (
                                            <Typography variant="body2">{detailTemplate.department.name}</Typography>
                                        ) : (
                                            <MissingValue variant="body2">Missing</MissingValue>
                                        )}
                                    </Box>
                                )}
                                {(detailTemplate.skill || detailMissing.includes("skill")) && (
                                    <Box>
                                        <DetailLabel>Skill</DetailLabel>
                                        {detailTemplate.skill ? (
                                            <Typography variant="body2">{detailTemplate.skill.name}</Typography>
                                        ) : (
                                            <MissingValue variant="body2">Missing</MissingValue>
                                        )}
                                    </Box>
                                )}
                            </MetaRow>
                            )}

                            {(detailTemplate.startDate || detailTemplate.dueDate || detailTemplate.templateStartOffset != null || detailTemplate.templateDueOffset != null || detailTemplate.templateStartRelative || detailTemplate.templateDueRelative) && (
                                <DetailSection>
                                    <DetailLabel>Scheduling</DetailLabel>
                                    <SchedulingRow>
                                        {detailTemplate.templateDateType === "relative" ? (
                                            <>
                                                {detailTemplate.templateStartRelative && (
                                                    <Typography variant="body2">Start: {formatRelativeAnchor(detailTemplate.templateStartRelative)}</Typography>
                                                )}
                                                {detailTemplate.templateDueRelative && (
                                                    <Typography variant="body2">Due: {formatRelativeAnchor(detailTemplate.templateDueRelative)}</Typography>
                                                )}
                                            </>
                                        ) : detailTemplate.templateDateType === "offset" ? (
                                            <>
                                                {detailTemplate.templateStartOffset != null && (
                                                    <Typography variant="body2">Start offset: {detailTemplate.templateStartOffset} days</Typography>
                                                )}
                                                {detailTemplate.templateDueOffset != null && (
                                                    <Typography variant="body2">Due offset: {detailTemplate.templateDueOffset} days</Typography>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {formatDate(detailTemplate.startDate) && (
                                                    <Typography variant="body2">Start: {formatDate(detailTemplate.startDate)}</Typography>
                                                )}
                                                {formatDate(detailTemplate.dueDate) && (
                                                    <Typography variant="body2">Due: {formatDate(detailTemplate.dueDate)}</Typography>
                                                )}
                                            </>
                                        )}
                                    </SchedulingRow>
                                </DetailSection>
                            )}

                            {detailTemplate.tags?.some((tag) => tag?.code) && (
                                <DetailSection>
                                    <DetailLabel>Tags</DetailLabel>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {detailTemplate.tags.filter((tag) => tag?.code).map((tag) => (
                                            <Chip key={tag._id} label={tag.isDeleted ? `${tagDisplayName(tag, canSeeInternal)} (deleted)` : tagDisplayName(tag, canSeeInternal)} size="small" variant="outlined" />
                                        ))}
                                    </Stack>
                                </DetailSection>
                            )}

                            {detailTemplate.projects?.length > 0 && (
                                <DetailSection>
                                    <DetailLabel>Child Projects ({detailTemplate.projects.length})</DetailLabel>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {detailTemplate.projects.map((p) => (
                                            <Chip key={p._id} label={p.name} size="small" color="secondary" variant="outlined" />
                                        ))}
                                    </Stack>
                                </DetailSection>
                            )}

                            {detailTemplate.tasks?.length > 0 && (
                                <DetailSection>
                                    <DetailLabel>Child Tasks ({detailTemplate.tasks.length})</DetailLabel>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {detailTemplate.tasks.map((t) => (
                                            <Chip key={t._id} label={t.name} size="small" color="success" variant="outlined" />
                                        ))}
                                    </Stack>
                                </DetailSection>
                            )}

                            {detailTemplate.payments?.length > 0 && (
                                <DetailSection>
                                    <DetailLabel>Payment Schedule</DetailLabel>
                                    {detailTemplate.payments.map((p, idx) => (
                                        <Typography key={idx} variant="body2">
                                            Payment {p.paymentNumber}: ${p.value} ({p.percentage}%)
                                        </Typography>
                                    ))}
                                    {detailTemplate.totalPayment && (
                                        <TotalPaymentText variant="body2" fontWeight="bold">
                                            Total: ${detailTemplate.totalPayment}
                                        </TotalPaymentText>
                                    )}
                                </DetailSection>
                            )}
                        </DetailCard>
                    ) : (
                        <EmptyDetail>
                            <Typography variant="body1">Select a template to view details</Typography>
                        </EmptyDetail>
                    )}
                </DetailPanel>
            </ContentArea>

            {executeModalOpen && selectedTemplate && (
                <TemplateExecuteModal
                    open={executeModalOpen}
                    template={selectedTemplate}
                    onClose={() => {
                        setExecuteModalOpen(false);
                        setSelectedTemplate(null);
                    }}
                    onExecute={handleExecuteConfirm}
                    executing={executing}
                />
            )}

            {editorOpen && (
                <TemplateEditorWizard
                    open={editorOpen}
                    mode={editorMode}
                    templateType={editorType}
                    initialTemplate={editorParent ? { ...editorTemplate, ...editorParent } : editorTemplate}
                    onClose={() => {
                        setEditorOpen(false);
                        setEditorTemplate(null);
                        setEditorParent(null);
                    }}
                    onSaved={() => {
                        const edited = editorTemplate;
                        setEditorOpen(false);
                        setEditorTemplate(null);
                        setEditorParent(null);
                        loadTemplates();
                        if (edited && detailTemplate?._id === edited._id) {
                            handleSelect(detailTemplate);
                        }
                    }}
                />
            )}

            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                title="Delete Template"
                message={`Are you sure you want to delete template "${deleteTarget?.name}"?`}
            />
        </PageContainer>
    );
};

export default Templates;
